/**
 * The Family tab: everything about the household that is a setting, not a
 * number.
 *
 * The other three tabs are places a parent goes every day. This is where
 * the rest lives — who is on the crew, who else can sign in, which tiles the
 * quick-log shows, the house rules, the fridge sheet — so that none of it
 * has to be a card on the home screen between a child and the gauge. It is
 * a list of rows rather than a grid of cards because every item here is a
 * place to go, and a row with a chevron says that where a card with a button
 * does not.
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { useFamily } from '../lib/family.jsx';
import { useFuel } from '../lib/fuel.js';
import { RowLink, RowList } from '../ui/Nav.jsx';

export default function Family() {
  const { family, kids, profile } = useFamily();
  const { signOut, user } = useAuth();
  const fuel = useFuel(family?.id);

  // A child has no business here; their bar doesn't offer it, but a typed
  // URL should land somewhere sensible rather than on a list of rule screens
  // that will each bounce them home in turn.
  if (profile) return <Navigate to="/" replace />;

  const crewDetail = kids.length === 0
    ? 'Nobody on board yet'
    : kids.map((k) => k.display_name).join(', ');

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <p className="eyebrow">Family</p>
        <h1>{family.name}</h1>
      </div>

      <section className="stack tight" aria-labelledby="family-crew">
        <h2 className="section-title" id="family-crew">Crew</h2>
        <RowList>
          <RowLink to="/children" title="The crew" detail={crewDetail} />
          {kids.length > 0 && (
            <RowLink to="/switch" title="Hand over to a child" detail="Pick who's flying" />
          )}
          <RowLink to="/invite" title="Invite another parent" detail="A second grown-up on the same family" />
        </RowList>
      </section>

      <section className="stack tight" aria-labelledby="family-fuel">
        <h2 className="section-title" id="family-fuel">Family Fuel</h2>
        {!fuel.loading && !fuel.envelope ? (
          <RowList>
            <RowLink to="/fuel/setup" title="Set up Family Fuel" detail="One pot for the fun stuff, topped up on a schedule" primary />
          </RowList>
        ) : (
          <RowList>
            <RowLink to="/fuel/tiles" title="Quick tiles" detail="What one tap logs" />
            <RowLink to="/fuel/print" title="Fridge sheet" detail="A one-page tally to print" />
          </RowList>
        )}
      </section>

      <section className="stack tight" aria-labelledby="family-wishes">
        <h2 className="section-title" id="family-wishes">Wishes</h2>
        <RowList>
          <RowLink to="/wishes/settings" title="House rules and waits" detail="How we spend, and how long a wish waits" />
        </RowList>
      </section>

      <div className="stack tight" style={{ marginTop: 'var(--space-4)' }}>
        <p className="small muted">Signed in as {user?.email}</p>
        <button className="quiet" onClick={signOut} style={{ alignSelf: 'flex-start' }}>Sign out</button>
      </div>
    </main>
  );
}
